import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { PeoplePicker } from '@/components/projects/PeoplePicker';
import { useAuth } from '@/app/AuthProvider';
import { useProfiles } from '@/features/users/hooks';
import { useCreateProject, useProjects } from '@/features/projects/hooks';
import { useCreateEngProject, useEngProjects } from '@/features/engineering/hooks';
import { ENG_STATUS_OPTIONS, suggestEngCode, type EngStatus } from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { inputDollarsToCents } from '@/lib/formatters';

interface NewEngProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (projectId: string) => void;
}

const NO_PM_PROJECT = '__none__';
const NEW_PM_PROJECT = '__new__';

/**
 * "New engineering project" — code, name, owner, team, dates, budget and
 * the optional Tasks tie-in (pick an existing task list, or spin up a
 * matching one tagged to the Engineering department in the same step).
 */
export function NewEngProjectDialog({ open, onOpenChange, onCreated }: NewEngProjectDialogProps) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { data: profiles } = useProfiles();
  const { data: pmProjects } = useProjects({ includeArchived: false });
  const { data: existing } = useEngProjects({ includeArchived: true });
  const createEng = useCreateEngProject();
  const createPm = useCreateProject();

  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<EngStatus>('concept');
  const [ownerId, setOwnerId] = useState<string>('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [budget, setBudget] = useState('');
  const [pmChoice, setPmChoice] = useState<string>(NO_PM_PROJECT);
  const [goToProject, setGoToProject] = useState(true);

  const suggestedCode = useMemo(
    () => suggestEngCode((existing ?? []).map((p) => p.code)),
    [existing],
  );

  useEffect(() => {
    if (!open) {
      setCode('');
      setCodeTouched(false);
      setName('');
      setDescription('');
      setStatus('concept');
      setOwnerId('');
      setMemberIds([]);
      setStartDate('');
      setTargetDate('');
      setBudget('');
      setPmChoice(NO_PM_PROJECT);
      setGoToProject(true);
    }
  }, [open]);

  useEffect(() => {
    if (open && !codeTouched) setCode(suggestedCode);
  }, [open, codeTouched, suggestedCode]);

  useEffect(() => {
    if (open && !ownerId && profile) setOwnerId(profile.id);
  }, [open, ownerId, profile]);

  const people = useMemo(
    () =>
      (profiles ?? [])
        .filter((p) => p.is_active !== false)
        .map((p) => ({ id: p.id, full_name: p.full_name, email: p.email, avatar_url: p.avatar_url })),
    [profiles],
  );

  const busy = createEng.isPending || createPm.isPending;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (!name.trim()) {
      toast.error('Give the project a name.');
      return;
    }
    let budgetCents: number | null = null;
    try {
      budgetCents = inputDollarsToCents(budget);
    } catch (err) {
      toast.error(formatError(err));
      return;
    }
    if (startDate && targetDate && targetDate < startDate) {
      toast.error('The target date can’t be before the start date.');
      return;
    }

    try {
      let pmProjectId: string | null = pmChoice === NO_PM_PROJECT || pmChoice === NEW_PM_PROJECT ? null : pmChoice;
      if (pmChoice === NEW_PM_PROJECT) {
        const pm = await createPm.mutateAsync({
          name: `${code.trim() || 'ENG'} · ${name.trim()}`,
          memberProfileIds: Array.from(new Set([...memberIds, ...(ownerId ? [ownerId] : [])])),
          department: 'engineering',
        });
        pmProjectId = pm.id;
      }
      const project = await createEng.mutateAsync({
        code: code.trim() || suggestedCode,
        name,
        description,
        status,
        owner_id: ownerId || null,
        start_date: startDate || null,
        target_date: targetDate || null,
        budget_cents: budgetCents,
        pm_project_id: pmProjectId,
        memberProfileIds: memberIds,
      });
      toast.success(`${project.code} created.`);
      onOpenChange(false);
      onCreated?.(project.id);
      if (goToProject) navigate(`/engineering/${project.id}`);
    } catch (err) {
      toast.error(formatError(err, 'Could not create the project.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>New engineering project</DialogTitle>
          <DialogDescription>
            A home for the design files, prints, costs and tasks of one engineered part or assembly.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
            <div className="space-y-1.5">
              <Label htmlFor="eng-code">Code</Label>
              <Input
                id="eng-code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setCodeTouched(true);
                }}
                className="font-mono"
                maxLength={32}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eng-name">Name</Label>
              <Input
                id="eng-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Bowl v2 — thicker rim"
                autoFocus
                maxLength={200}
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="eng-description">What are we building?</Label>
            <Textarea
              id="eng-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Goal, constraints, what success looks like…"
              className="min-h-[72px]"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Select value={ownerId} onValueChange={setOwnerId}>
                <SelectTrigger aria-label="Project owner">
                  <SelectValue placeholder="Pick an owner" />
                </SelectTrigger>
                <SelectContent>
                  {people.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.full_name ?? p.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Starting status</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as EngStatus)}>
                <SelectTrigger aria-label="Status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENG_STATUS_OPTIONS.filter((o) => !['design_locked', 'production', 'cancelled'].includes(o.value)).map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Team</Label>
            <PeoplePicker
              profiles={people.filter((p) => p.id !== ownerId)}
              value={memberIds.filter((id) => id !== ownerId)}
              onChange={setMemberIds}
              placeholder="Add engineers, designers, technicians…"
            />
            <p className="text-xs text-muted-foreground">
              Everyone with the Engineering section can view the project; the team (and the owner) can edit it.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Start date</Label>
              <DatePicker value={startDate} onChange={setStartDate} placeholder="Start" aria-label="Start date" />
            </div>
            <div className="space-y-1.5">
              <Label>Target date</Label>
              <DatePicker value={targetDate} onChange={setTargetDate} placeholder="Target" aria-label="Target date" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eng-budget">Budget</Label>
              <Input
                id="eng-budget"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="$ 0.00"
                inputMode="decimal"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Task list (Tasks section)</Label>
            <Select value={pmChoice} onValueChange={setPmChoice}>
              <SelectTrigger aria-label="Task list">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PM_PROJECT}>No task list yet</SelectItem>
                <SelectItem value={NEW_PM_PROJECT}>Create a matching task list in Tasks</SelectItem>
                {(pmProjects ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The Tasks tab shows the linked project’s task list. You can change this later.
            </p>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
            <Label htmlFor="eng-goto" className="font-normal">
              Open the project after creating it
            </Label>
            <Switch id="eng-goto" checked={goToProject} onCheckedChange={setGoToProject} />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
